// Załączniki 2.0 — WYŚWIETLANIE: zdjęcie / film / album w dymku wiadomości, miniatury z leniwym ładowaniem, galeria na pełnym ekranie.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Play, Film } from "lucide-react";

// ---------------------------------------------------------------- adresy
const NAME_OK = /^[A-Za-z0-9._-]{1,200}$/;
function baseName(url) {
  const m = /^\/(?:uploads|f)\/([^/?#]+)$/.exec(url || "");
  return m && NAME_OK.test(m[1]) ? m[1] : null;
}
export function mediaSrc(url) {
  const n = baseName(url);
  return n ? `/f/${n}` : url;
}
export function thumbSrc(url, w) {
  const n = baseName(url);
  return n ? `/img/${w}/${n}` : url;
}
function fallbackTo(url) {
  return (e) => {
    const el = e.currentTarget;
    if (!el.dataset.fb) {
      el.dataset.fb = "1";
      el.src = url;
    }
  };
}

// ---------------------------------------------------------------- wyświetlanie
function fmtDur(s) {
  if (!s) return "";
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
const ratioOf = (it) => (it.w && it.h ? Math.max(0.62, Math.min(1.9, it.w / it.h)) : null);

function Tile({ item, w, onOpen, className, more, style }) {
  const isVideo = item.type === "video" || (item.mimeType || "").startsWith("video/");
  const src = isVideo ? (item.poster ? thumbSrc(item.poster, w) : null) : thumbSrc(item.url, w);
  const [loaded, setLoaded] = useState(false);
  return (
    <button type="button" style={style} className={`mx-tile ${className || ""} ${loaded ? "ld" : ""}`} onClick={() => onOpen()} aria-label={isVideo ? "Odtwórz film" : "Powiększ zdjęcie"}>
      {src ? (
        <img src={src} alt="" loading="lazy" decoding="async" onLoad={() => setLoaded(true)} onError={fallbackTo(isVideo ? item.poster : item.url)} />
      ) : (
        <span className="mx-ph"><Film size={26} /></span>
      )}
      {isVideo && (
        <span className="mx-play">
          <Play size={20} fill="currentColor" />
          {item.duration ? <em>{fmtDur(item.duration)}</em> : null}
        </span>
      )}
      {more > 0 && <span className="mx-more">+{more}</span>}
    </button>
  );
}

export function Gallery({ items, start, onClose }) {
  const [idx, setIdx] = useState(start || 0);
  const [dx, setDx] = useState(0);
  const dxRef = useRef(0);
  const touch = useRef(null);
  const n = items.length;
  const go = (d) => setIdx((i) => Math.max(0, Math.min(n - 1, i + d)));
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n]);
  useEffect(() => {
    // dociągnij sąsiednie zdjęcia, żeby przesuwanie było płynne
    [idx - 1, idx + 1].forEach((j) => {
      const it = items[j];
      if (it && it.type !== "video") new Image().src = mediaSrc(it.url);
    });
  }, [idx, items]);
  const it = items[idx];
  const isVideo = it.type === "video" || (it.mimeType || "").startsWith("video/");
  const full = mediaSrc(it.url);
  return createPortal(
    <div
      className="gx"
      onClick={onClose}
      onTouchStart={(e) => { if (e.touches.length === 1) touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }}
      onTouchMove={(e) => {
        if (!touch.current || e.touches.length !== 1) return;
        const mx = e.touches[0].clientX - touch.current.x;
        const my = e.touches[0].clientY - touch.current.y;
        if (Math.abs(mx) > Math.abs(my)) {
          dxRef.current = mx;
          setDx(mx);
        }
      }}
      onTouchEnd={() => {
        if (touch.current && Math.abs(dxRef.current) > 60) go(dxRef.current < 0 ? 1 : -1);
        touch.current = null;
        dxRef.current = 0;
        setDx(0);
      }}
    >
      <button className="gx-close" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Zamknij"><X size={22} /></button>
      {n > 1 && <div className="gx-count">{idx + 1} / {n}</div>}
      <div className="gx-stage" style={{ transform: dx ? `translateX(${dx}px)` : undefined, transition: dx ? "none" : "transform 0.2s ease" }} onClick={(e) => e.stopPropagation()}>
        {isVideo ? (
          <video key={it.url} src={full} poster={it.poster ? thumbSrc(it.poster, 720) : undefined} controls autoPlay playsInline className="gx-media" onError={fallbackTo(it.url)} />
        ) : (
          <img key={it.url} src={full} alt={it.filename || ""} className="gx-media" style={{ backgroundImage: `url("${thumbSrc(it.url, 360)}")` }} onError={fallbackTo(it.url)} />
        )}
      </div>
      {idx > 0 && <button className="gx-nav gx-prev" onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Poprzednie"><ChevronLeft size={26} /></button>}
      {idx < n - 1 && <button className="gx-nav gx-next" onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Następne"><ChevronRight size={26} /></button>}
    </div>,
    document.body
  );
}

// zdjęcie / film / album w dymku wiadomości
export function MediaAttachment({ attachment }) {
  const [open, setOpen] = useState(null);
  const items = Array.isArray(attachment.items) && attachment.items.length ? attachment.items : [attachment];
  const n = items.length;
  const gallery = open !== null ? <Gallery items={items} start={open} onClose={() => setOpen(null)} /> : null;

  if (n === 1) {
    const it = items[0];
    const isVideo = it.type === "video" || (it.mimeType || "").startsWith("video/");
    const r = ratioOf(it);
    if (isVideo) {
      return (
        <div className="mx-one mx-video" style={r ? { aspectRatio: String(r) } : undefined}>
          <video
            src={mediaSrc(it.url)}
            poster={it.poster ? thumbSrc(it.poster, 540) : undefined}
            controls
            playsInline
            preload={it.poster ? "none" : "metadata"}
            className="chat-attachment-video"
            onError={fallbackTo(it.url)}
          />
        </div>
      );
    }
    if (r) {
      return (
        <>
          <Tile item={it} w={540} className="mx-one" style={{ aspectRatio: String(r) }} onOpen={() => setOpen(0)} />
          {gallery}
        </>
      );
    }
    // starsze zdjęcia bez znanych wymiarów: jak dotąd, tylko mniejsza miniatura i leniwe ładowanie
    return (
      <>
        <img
          src={thumbSrc(it.url, 720)}
          alt={it.filename}
          className="chat-attachment-img"
          loading="lazy"
          decoding="async"
          onClick={() => setOpen(0)}
          onError={fallbackTo(it.url)}
        />
        {gallery}
      </>
    );
  }
  const shown = n <= 4 ? items : items.slice(0, 6);
  const extra = n - shown.length;
  return (
    <>
      <div className={`mx-album mx-n${Math.min(shown.length, 6)}`}>
        {shown.map((it, i) => (
          <Tile key={i} item={it} w={n <= 2 ? 360 : 240} onOpen={() => setOpen(i)} more={i === shown.length - 1 ? extra : 0} />
        ))}
      </div>
      {gallery}
    </>
  );
}
